import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs, { Dayjs } from 'dayjs';
import { useSnackbar } from 'notistack';
import { useSearchParams } from 'react-router-dom';
import { useTenant } from '../components/ContextTenant.tsx';
import { useReservations } from '../hooks/useReservations.ts';
import apiService from '../services/apiService.ts';

type ReservationRow = {
  id: number;
  tableId?: number | null;
  tableName?: string | null;
  customerId?: number | null;
  customerName?: string | null;
  numberOfPeople?: number | null;
  requestedTime?: string | null;
  status?: string | null;
  specialRequests?: string | null;
  tenantId?: number | null;
  createdAt?: string | null;
  paymentStatus?: string | null;
  bookingStatus?: string | null;
  source?: string | null;
};

type LocationOption = {
  id: number;
  name: string;
};

type SpaceOption = {
  id: number;
  name: string;
};

type TableOption = {
  id: number;
  name: string;
  seats?: number | null;
  status?: string | null;
};

type CreateReservationDialogProps = {
  open: boolean;
  tenantId: string;
  onClose: () => void;
  onCreated: () => Promise<void> | void;
};

const STATUS_COLOR: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  PENDING: 'warning',
  CONFIRMED: 'success',
  REJECTED: 'error',
  CANCELLED: 'default',
  COMPLETED: 'info',
  EXPIRED: 'default',
};

const SOURCE_LABEL: Record<string, string> = {
  PUBLIC_WIDGET: 'Widget public',
  PHONE: 'Telefon',
  WALK_IN: 'Walk-in',
  MANUAL_OTHER: 'Alt manual',
};

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return '-';
  }

  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('DD.MM.YYYY, HH:mm') : value;
};

const toLocalDateTimeString = (value: Dayjs) => value.format('YYYY-MM-DDTHH:mm:ss');

const resolveErrorMessage = (error: any, fallback: string) =>
  error?.response?.data?.message
  || error?.response?.data?.error
  || error?.message
  || fallback;

const isPendingReservation = (status?: string | null) => status === 'PENDING';
const canCancelReservation = (status?: string | null) => status === 'PENDING' || status === 'CONFIRMED';

const CreateReservationDialog: React.FC<CreateReservationDialogProps> = ({ open, tenantId, onClose, onCreated }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [locations, setLocations] = useState<LocationOption[]>([]);
  const [spaces, setSpaces] = useState<SpaceOption[]>([]);
  const [tables, setTables] = useState<TableOption[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    locationId: '',
    spaceId: '',
    tableId: '',
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    numberOfPeople: '2',
    source: 'PHONE',
    specialRequests: '',
  });
  const [requestedTime, setRequestedTime] = useState<Dayjs | null>(
    dayjs().add(1, 'day').hour(19).minute(0).second(0).millisecond(0)
  );

  useEffect(() => {
    if (!open || !tenantId) {
      return;
    }

    let active = true;
    setLoadingOptions(true);
    setForm({
      locationId: '',
      spaceId: '',
      tableId: '',
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      numberOfPeople: '2',
      source: 'PHONE',
      specialRequests: '',
    });
    setRequestedTime(dayjs().add(1, 'day').hour(19).minute(0).second(0).millisecond(0));

    void apiService.getLocations(tenantId)
      .then((result: LocationOption[]) => {
        if (!active) {
          return;
        }
        setLocations(result ?? []);
        if (result?.[0]?.id) {
          setForm((current) => ({ ...current, locationId: String(result[0].id) }));
        }
      })
      .catch((error: any) => {
        if (!active) {
          return;
        }
        enqueueSnackbar(resolveErrorMessage(error, 'Nu am putut incarca locatiile tenantului.'), { variant: 'error' });
      })
      .finally(() => {
        if (active) {
          setLoadingOptions(false);
        }
      });

    return () => {
      active = false;
    };
  }, [enqueueSnackbar, open, tenantId]);

  useEffect(() => {
    if (!open || !form.locationId) {
      setSpaces([]);
      setTables([]);
      setForm((current) => ({ ...current, spaceId: '', tableId: '' }));
      return;
    }

    let active = true;
    setLoadingOptions(true);

    void apiService.getSpaces(form.locationId)
      .then((result: SpaceOption[]) => {
        if (!active) {
          return;
        }
        setSpaces(result ?? []);
        const nextSpaceId = result?.[0]?.id ? String(result[0].id) : '';
        setForm((current) => ({
          ...current,
          spaceId: current.locationId === form.locationId ? (current.spaceId || nextSpaceId) : nextSpaceId,
          tableId: '',
        }));
      })
      .catch((error: any) => {
        if (!active) {
          return;
        }
        enqueueSnackbar(resolveErrorMessage(error, 'Nu am putut incarca spatiile locatiei.'), { variant: 'error' });
      })
      .finally(() => {
        if (active) {
          setLoadingOptions(false);
        }
      });

    return () => {
      active = false;
    };
  }, [enqueueSnackbar, form.locationId, open]);

  useEffect(() => {
    if (!open || !form.spaceId) {
      setTables([]);
      setForm((current) => ({ ...current, tableId: '' }));
      return;
    }

    let active = true;
    setLoadingOptions(true);

    void apiService.getTables(form.spaceId)
      .then((result: TableOption[]) => {
        if (!active) {
          return;
        }
        setTables(result ?? []);
        const nextTableId = result?.[0]?.id ? String(result[0].id) : '';
        setForm((current) => ({
          ...current,
          tableId: current.spaceId === form.spaceId ? (current.tableId || nextTableId) : nextTableId,
        }));
      })
      .catch((error: any) => {
        if (!active) {
          return;
        }
        enqueueSnackbar(resolveErrorMessage(error, 'Nu am putut incarca mesele spatiului.'), { variant: 'error' });
      })
      .finally(() => {
        if (active) {
          setLoadingOptions(false);
        }
      });

    return () => {
      active = false;
    };
  }, [enqueueSnackbar, form.spaceId, open]);

  const handleFieldChange = (field: keyof typeof form) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.value;
    setForm((current) => {
      if (field === 'locationId') {
        return { ...current, locationId: value, spaceId: '', tableId: '' };
      }
      if (field === 'spaceId') {
        return { ...current, spaceId: value, tableId: '' };
      }
      return { ...current, [field]: value };
    });
  };

  const handleSubmit = async () => {
    if (!tenantId) {
      enqueueSnackbar('Selecteaza un tenant inainte sa creezi rezervarea.', { variant: 'warning' });
      return;
    }
    if (!form.locationId || !form.spaceId || !form.tableId) {
      enqueueSnackbar('Selecteaza locatia, spatiul si masa.', { variant: 'warning' });
      return;
    }
    if (!form.firstName.trim() || !form.lastName.trim() || !form.phone.trim()) {
      enqueueSnackbar('Completeaza numele si telefonul clientului.', { variant: 'warning' });
      return;
    }
    if (!requestedTime || !requestedTime.isValid()) {
      enqueueSnackbar('Alege o data valida pentru rezervare.', { variant: 'warning' });
      return;
    }

    setSaving(true);
    try {
      const customer = await apiService.createGuestCustomer({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || undefined,
        tenantId: Number(tenantId),
      });

      await apiService.createReservation(tenantId, {
        tenantId: Number(tenantId),
        tableId: Number(form.tableId),
        customerId: customer.id,
        numberOfPeople: Number(form.numberOfPeople),
        requestedTime: toLocalDateTimeString(requestedTime),
        specialRequests: form.specialRequests.trim() || undefined,
        source: form.source,
        status: 'PENDING',
      });

      enqueueSnackbar('Rezervarea a fost creata din 3100.', { variant: 'success' });
      await onCreated();
      onClose();
    } catch (error: any) {
      enqueueSnackbar(resolveErrorMessage(error, 'Nu am putut crea rezervarea.'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>Rezervare noua din suport</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Creezi rezervarea direct pe tenantul selectat, folosind aceeasi infrastructura ca managerul locatiei.
          </Typography>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              select
              label="Locatie"
              value={form.locationId}
              onChange={handleFieldChange('locationId')}
              fullWidth
              disabled={loadingOptions || saving || locations.length === 0}
            >
              {locations.map((location) => (
                <MenuItem key={location.id} value={String(location.id)}>
                  {location.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Spatiu"
              value={form.spaceId}
              onChange={handleFieldChange('spaceId')}
              fullWidth
              disabled={loadingOptions || saving || spaces.length === 0}
            >
              {spaces.map((space) => (
                <MenuItem key={space.id} value={String(space.id)}>
                  {space.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Masa"
              value={form.tableId}
              onChange={handleFieldChange('tableId')}
              fullWidth
              disabled={loadingOptions || saving || tables.length === 0}
            >
              {tables.map((table) => (
                <MenuItem key={table.id} value={String(table.id)}>
                  {table.name} {table.seats ? `(${table.seats} locuri)` : ''} {table.status ? `- ${table.status}` : ''}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField label="Prenume" value={form.firstName} onChange={handleFieldChange('firstName')} fullWidth />
            <TextField label="Nume" value={form.lastName} onChange={handleFieldChange('lastName')} fullWidth />
            <TextField label="Telefon" value={form.phone} onChange={handleFieldChange('phone')} fullWidth />
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField label="Email" value={form.email} onChange={handleFieldChange('email')} fullWidth />
            <TextField
              label="Numar persoane"
              value={form.numberOfPeople}
              onChange={handleFieldChange('numberOfPeople')}
              type="number"
              inputProps={{ min: 1 }}
              fullWidth
            />
            <TextField select label="Sursa" value={form.source} onChange={handleFieldChange('source')} fullWidth>
              <MenuItem value="PHONE">Telefon</MenuItem>
              <MenuItem value="WALK_IN">Walk-in</MenuItem>
              <MenuItem value="MANUAL_OTHER">Alt manual</MenuItem>
            </TextField>
          </Stack>

          <DateTimePicker
            label="Ora rezervarii"
            value={requestedTime}
            onChange={setRequestedTime}
            disablePast
            sx={{ width: '100%' }}
          />

          <TextField
            label="Cerinte speciale"
            value={form.specialRequests}
            onChange={handleFieldChange('specialRequests')}
            fullWidth
            multiline
            minRows={3}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Renunta</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={saving || loadingOptions}>
          {saving ? 'Se creeaza...' : 'Creeaza rezervarea'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ReservationsPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [params] = useSearchParams();
  const { tenantId: scopedTenantId } = useTenant();
  const tenantId = params.get('tenantId') || (scopedTenantId ? String(scopedTenantId) : '');
  const [from, setFrom] = useState<Dayjs | null>(null);
  const [to, setTo] = useState<Dayjs | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [pendingActionKey, setPendingActionKey] = useState<string | null>(null);

  const { rows, loading, refetch } = useReservations(
    tenantId,
    from ? from.toISOString() : undefined,
    to ? to.toISOString() : undefined
  );

  const typedRows = rows as ReservationRow[];
  const pendingCount = typedRows.filter((row) => row.status === 'PENDING').length;
  const confirmedCount = typedRows.filter((row) => row.status === 'CONFIRMED').length;
  const cancelledCount = typedRows.filter((row) => row.status === 'CANCELLED' || row.status === 'REJECTED').length;

  const runAction = async (row: ReservationRow, action: 'confirm' | 'reject' | 'cancel') => {
    if (!tenantId || !row.id) {
      return;
    }

    const actionKey = `${action}-${row.id}`;
    setPendingActionKey(actionKey);
    try {
      if (action === 'confirm') {
        await apiService.confirmReservation(tenantId, row.id);
        enqueueSnackbar(`Rezervarea #${row.id} a fost aprobata din 3100.`, { variant: 'success' });
      } else if (action === 'reject') {
        await apiService.rejectReservation(tenantId, row.id);
        enqueueSnackbar(`Rezervarea #${row.id} a fost respinsa din 3100.`, { variant: 'success' });
      } else {
        await apiService.cancelReservation(tenantId, row.id);
        enqueueSnackbar(`Rezervarea #${row.id} a fost anulata din 3100.`, { variant: 'success' });
      }
      await refetch(true);
    } catch (error: any) {
      enqueueSnackbar(resolveErrorMessage(error, `Nu am putut procesa rezervarea #${row.id}.`), { variant: 'error' });
    } finally {
      setPendingActionKey(null);
    }
  };

  const cols: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 90 },
    {
      field: 'customerName',
      headerName: 'Client',
      minWidth: 220,
      flex: 1.1,
      valueGetter: ({ row }) => row.customerName || '-',
    },
    {
      field: 'tableName',
      headerName: 'Masa',
      minWidth: 140,
      flex: 0.7,
      valueGetter: ({ row }) => row.tableName || '-',
    },
    {
      field: 'requestedTime',
      headerName: 'Data',
      minWidth: 190,
      flex: 0.95,
      valueFormatter: ({ value }) => formatDateTime(value as string | null | undefined),
    },
    {
      field: 'numberOfPeople',
      headerName: 'Persoane',
      width: 110,
      align: 'center',
      headerAlign: 'center',
      valueGetter: ({ row }) => row.numberOfPeople ?? '-',
    },
    {
      field: 'source',
      headerName: 'Sursa',
      minWidth: 150,
      flex: 0.8,
      valueGetter: ({ row }) => SOURCE_LABEL[row.source || ''] || row.source || '-',
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 150,
      flex: 0.8,
      renderCell: (params: GridRenderCellParams<any, string>) => (
        <Chip
          label={params.value || '-'}
          size="small"
          color={STATUS_COLOR[params.value || ''] || 'default'}
          variant={params.value === 'PENDING' ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      field: 'paymentStatus',
      headerName: 'Plata',
      minWidth: 120,
      flex: 0.7,
      valueGetter: ({ row }) => row.paymentStatus || '-',
    },
    {
      field: 'bookingStatus',
      headerName: 'Booking',
      minWidth: 130,
      flex: 0.8,
      valueGetter: ({ row }) => row.bookingStatus || '-',
    },
    {
      field: 'specialRequests',
      headerName: 'Detalii',
      minWidth: 240,
      flex: 1.2,
      valueGetter: ({ row }) => row.specialRequests || '-',
    },
    {
      field: 'actions',
      headerName: 'Actiuni suport',
      minWidth: 300,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<any, ReservationRow>) => {
        const row = params.row as ReservationRow;
        return (
          <Stack direction="row" spacing={1} sx={{ py: 1 }}>
            <Button
              size="small"
              variant="contained"
              disabled={!isPendingReservation(row.status) || pendingActionKey === `confirm-${row.id}`}
              onClick={() => void runAction(row, 'confirm')}
            >
              {pendingActionKey === `confirm-${row.id}` ? 'Se aproba...' : 'Aproba'}
            </Button>
            <Button
              size="small"
              color="warning"
              variant="outlined"
              disabled={!isPendingReservation(row.status) || pendingActionKey === `reject-${row.id}`}
              onClick={() => void runAction(row, 'reject')}
            >
              {pendingActionKey === `reject-${row.id}` ? 'Se respinge...' : 'Respinge'}
            </Button>
            <Button
              size="small"
              color="error"
              variant="outlined"
              disabled={!canCancelReservation(row.status) || pendingActionKey === `cancel-${row.id}`}
              onClick={() => void runAction(row, 'cancel')}
            >
              {pendingActionKey === `cancel-${row.id}` ? 'Se anuleaza...' : 'Anuleaza'}
            </Button>
          </Stack>
        );
      },
    },
  ];

  if (!tenantId) {
    return (
      <Box p={2}>
        <Alert severity="info">
          Selecteaza mai intai tenantul din header-ul 3100 ca sa poti interveni pe rezervarile lui.
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={2}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h5" gutterBottom>
            Rezervari restaurant
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Din 3100 poti crea, aproba, respinge sau anula rezervari pentru tenantul selectat, exact pentru scenariile de suport.
          </Typography>
        </Box>

        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} justifyContent="space-between">
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <DatePicker label="From" value={from} onChange={setFrom} />
            <DatePicker label="To" value={to} onChange={setTo} />
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip label={`${typedRows.length} total`} variant="outlined" />
            <Chip label={`${pendingCount} pending`} color="warning" variant="outlined" />
            <Chip label={`${confirmedCount} confirmate`} color="success" variant="outlined" />
            <Chip label={`${cancelledCount} inchise`} variant="outlined" />
            <Button variant="contained" onClick={() => setCreateOpen(true)}>
              Rezervare noua
            </Button>
          </Stack>
        </Stack>

        {loading ? (
          <Box py={6} display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            rows={typedRows}
            columns={cols}
            autoHeight
            disableRowSelectionOnClick
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 25,
                  page: 0,
                },
              },
            }}
            getRowHeight={() => 'auto'}
            sx={{
              '& .MuiDataGrid-cell': {
                alignItems: 'center',
              },
            }}
          />
        )}
      </Stack>

      <CreateReservationDialog
        open={createOpen}
        tenantId={tenantId}
        onClose={() => setCreateOpen(false)}
        onCreated={async () => {
          await refetch(true);
        }}
      />
    </Box>
  );
};

export default ReservationsPage;
